import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { businessesAPI, categoriesAPI } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import { formatApiError } from '../utils/errorMessage';

/**
 * Onboarding Wizard Component
 * Single Responsibility: Guide new users through initial setup
 */
const Onboarding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Step 1: Business Information
  const [businessForm, setBusinessForm] = useState({
    name: '',
    legal_name: '',
    legal_structure: 'LLC',
    tax_id: '',
    industry: '',
    address_line1: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: ''
  });
  
  // Step 2: Business Logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Step 3: Initial Categories
  const [selectedCategories, setSelectedCategories] = useState([]);
  const defaultCategories = [
    { name: 'Office Supplies', type: 'expense', selected: true },
    { name: 'Utilities', type: 'expense', selected: true },
    { name: 'Rent', type: 'expense', selected: true },
    { name: 'Salary', type: 'expense', selected: true },
    { name: 'Sales', type: 'income', selected: true },
    { name: 'Services', type: 'income', selected: true },
    { name: 'Consulting', type: 'income', selected: true }
  ];

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const businessData = {
        ...businessForm,
        country: 'USA'
      };
      
      const res = await businessesAPI.create(businessData);
      
      if (logoFile && res.data.id) {
        await businessesAPI.uploadLogo(res.data.id, logoFile);
      }
      
      // Create default categories
      if (selectedCategories.length > 0) {
        for (const cat of selectedCategories) {
          try {
            await categoriesAPI.create({ name: cat.name, type: cat.type });
          } catch (err) {
            console.warn('Failed to create category:', cat.name);
          }
        }
      }
      
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err, 'Failed to create business'));
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleCategory = (index) => {
    const updated = [...selectedCategories];
    updated[index] = { ...updated[index], selected: !updated[index].selected };
    setSelectedCategories(updated);
  };

  // Initialize selected categories
  React.useEffect(() => {
    setSelectedCategories(defaultCategories);
  }, []);

  const skipOnboarding = () => {
    navigate('/dashboard');
  };

  return (
    <ErrorBoundary section="Onboarding">
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to eloNest!</h1>
            <p className="text-gray-600">Let's get you set up in just a few steps</p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Step {step} of 3</span>
              <button
                onClick={skipOnboarding}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Skip for now
              </button>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(step / 3) * 100}%` }}
              ></div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded" role="alert">
              {error}
            </div>
          )}

          {/* Step 1: Business Information */}
          {step === 1 && (
            <div>
              <h2 className="text-2xl font-semibold mb-4">Business Information</h2>
              <form onSubmit={(e) => { e.preventDefault(); setStep(2); }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={businessForm.name}
                      onChange={(e) => setBusinessForm({ ...businessForm, name: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="My Business"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Legal Structure
                    </label>
                    <select
                      value={businessForm.legal_structure}
                      onChange={(e) => setBusinessForm({ ...businessForm, legal_structure: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    >
                      <option value="LLC">LLC</option>
                      <option value="Corporation">Corporation</option>
                      <option value="S-Corp">S-Corp</option>
                      <option value="Sole Proprietorship">Sole Proprietorship</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Personal">Personal</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Legal Name
                    </label>
                    <input
                      type="text"
                      value={businessForm.legal_name}
                      onChange={(e) => setBusinessForm({ ...businessForm, legal_name: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="Legal registered name"
                    />
                  </div>
                  {businessForm.legal_structure !== 'Personal' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax ID (EIN/SSN)
                    </label>
                    <input
                      type="text"
                      value={businessForm.tax_id}
                      onChange={(e) => setBusinessForm({ ...businessForm, tax_id: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="12-3456789"
                    />
                  </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Industry
                    </label>
                    <input
                      type="text"
                      value={businessForm.industry}
                      onChange={(e) => setBusinessForm({ ...businessForm, industry: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="e.g., Technology, Retail, Services"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={businessForm.phone}
                      onChange={(e) => setBusinessForm({ ...businessForm, phone: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={businessForm.address_line1}
                      onChange={(e) => setBusinessForm({ ...businessForm, address_line1: e.target.value })}
                      className="w-full border rounded px-3 py-2 mb-2"
                      placeholder="Street address"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={businessForm.city}
                        onChange={(e) => setBusinessForm({ ...businessForm, city: e.target.value })}
                        className="border rounded px-3 py-2"
                        placeholder="City"
                      />
                      <input
                        type="text"
                        value={businessForm.state}
                        onChange={(e) => setBusinessForm({ ...businessForm, state: e.target.value })}
                        className="border rounded px-3 py-2"
                        placeholder="State"
                      />
                      <input
                        type="text"
                        value={businessForm.zip_code}
                        onChange={(e) => setBusinessForm({ ...businessForm, zip_code: e.target.value })}
                        className="border rounded px-3 py-2"
                        placeholder="ZIP"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={skipOnboarding}
                    className="px-4 py-2 border rounded hover:bg-gray-50"
                  >
                    Skip
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Next: Add Logo
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Step 2: Business Logo */}
          {step === 2 && (
            <div>
              <h2 className="text-2xl font-semibold mb-4">Business Logo (Optional)</h2>
              <p className="text-gray-600 mb-6">Upload your business logo to use on invoices and reports</p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                {logoPreview ? (
                  <div>
                    <img src={logoPreview} alt="Logo preview" className="max-h-32 mx-auto mb-4" />
                    <button
                      onClick={() => {
                        setLogoFile(null);
                        setLogoPreview(null);
                      }}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <label className="cursor-pointer">
                      <div className="text-gray-400 mb-2">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-sm text-gray-600">Click to upload logo</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                )}
              </div>

              <div className="flex justify-between gap-3 mt-6">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Next: Categories
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Default Categories */}
          {step === 3 && (
            <div>
              <h2 className="text-2xl font-semibold mb-4">Default Categories</h2>
              <p className="text-gray-600 mb-6">Select which default categories you'd like to use</p>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {selectedCategories.map((cat, index) => (
                  <label key={index} className="flex items-center p-3 border rounded hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cat.selected}
                      onChange={() => toggleCategory(index)}
                      className="mr-3"
                    />
                    <span className="flex-1">{cat.name}</span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      cat.type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {cat.type}
                    </span>
                  </label>
                ))}
              </div>

              <div className="flex justify-between gap-3 mt-6">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Back
                </button>
                <button
                  onClick={handleBusinessSubmit}
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {loading ? 'Setting up...' : 'Complete Setup'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Onboarding;

